import { useEffect, useState } from "react";
import { fr } from "date-fns/locale";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromIso = (s) => (s && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : undefined);
const toFr = (s) => (s ? s.split("-").reverse().join("/") : "");
const parseTyped = (t) => {
  const m = t.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/) || t.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [d, mo, y] = m[1].length === 4 ? [m[3], m[2], m[1]] : [m[1], m[2], m[3]];
  const dt = new Date(+y, +mo - 1, +d, 12);
  return dt.getDate() === +d && dt.getMonth() === +mo - 1 ? toIso(dt) : null;
};
const emit = (onChange, value) => onChange?.({ target: { value }, currentTarget: { value } });
const CAL_CLS = { caption_label: "hidden", caption_dropdowns: "flex gap-1.5 justify-center px-8", dropdown: "text-sm border border-slate-300 rounded-md px-1.5 py-1 bg-white capitalize",
  vhidden: "sr-only", day_selected: "!bg-slate-900 !text-white hover:!bg-slate-800", day_today: "ring-1 ring-amber-400 font-bold" };

export const DateInput = ({ value, onChange, className = "", min, max, disabled, required, placeholder = "jj/mm/aaaa", "data-testid": testid, ...rest }) => {
  const [text, setText] = useState(toFr(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { setText(toFr(value)); }, [value]);
  const commit = (t) => {
    if (!t.trim()) return value && emit(onChange, "");
    const iso = parseTyped(t);
    if (iso && (!min || iso >= min) && (!max || iso <= max)) { if (iso !== value) emit(onChange, iso); setText(toFr(iso)); }
    else setText(toFr(value));
  };
  const sel = fromIso(value);
  const disabledDays = [min && { before: fromIso(min) }, max && { after: fromIso(max) }].filter(Boolean);
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <div className="relative">
        <input {...rest} data-testid={testid} type="text" inputMode="numeric" autoComplete="off" value={text} placeholder={placeholder} disabled={disabled} required={required}
          className={`${className} !pr-9 min-w-[8.5rem]`} onChange={(e) => { setText(e.target.value); const iso = parseTyped(e.target.value); if (iso && e.target.value.length >= 10 && iso !== value && (!min || iso >= min) && (!max || iso <= max)) emit(onChange, iso); }}
          onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), commit(e.currentTarget.value))} />
        <PopoverTrigger asChild>
          <button type="button" disabled={disabled} data-testid={testid ? `${testid}-calendar-btn` : undefined} aria-label="Ouvrir le calendrier"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-slate-500 hover:text-slate-900 hover:bg-slate-100 disabled:opacity-40"><CalendarDays className="h-4 w-4" /></button>
        </PopoverTrigger>
      </div>
      <PopoverContent className="w-auto p-0 z-[300]" align="start" collisionPadding={8}>
        <Calendar mode="single" locale={fr} weekStartsOn={1} selected={sel} defaultMonth={sel || fromIso(min) || new Date()} disabled={disabledDays}
          captionLayout="dropdown-buttons" fromYear={1930} toYear={new Date().getFullYear() + 10} classNames={CAL_CLS}
          onSelect={(d) => { if (d) { emit(onChange, toIso(d)); setOpen(false); } }} />
        <div className="flex justify-between border-t border-slate-100 px-3 py-2 text-xs font-semibold">
          <button type="button" className="text-slate-500 hover:text-slate-900" onClick={() => { emit(onChange, ""); setOpen(false); }}>Effacer</button>
          <button type="button" className="text-sky-700 hover:text-sky-900 disabled:opacity-30 disabled:cursor-not-allowed" disabled={(min && toIso(new Date()) < min) || (max && toIso(new Date()) > max)} onClick={() => { emit(onChange, toIso(new Date())); setOpen(false); }}>Aujourd'hui</button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const MONTHS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
const MONTHS_LONG = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export const MonthInput = ({ value, onChange, className = "", min, max, disabled, "data-testid": testid }) => {
  const [open, setOpen] = useState(false);
  const [y, setY] = useState(() => +(value || toIso(new Date())).slice(0, 4));
  useEffect(() => { if (value) setY(+value.slice(0, 4)); }, [value]);
  const label = value ? `${MONTHS_LONG[+value.slice(5, 7) - 1]} ${value.slice(0, 4)}` : "Choisir un mois";
  return (
    <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} disabled={disabled} className={`${className} inline-flex items-center gap-2 capitalize text-left`}>
          <CalendarDays className="h-4 w-4 text-slate-500 shrink-0" /><span>{label}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 z-[300]" align="start" collisionPadding={8}>
        <div className="flex items-center justify-between mb-2">
          <button type="button" data-testid={testid ? `${testid}-prev-year` : undefined} onClick={() => setY(y - 1)} className="p-1 rounded hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-bold text-sm">{y}</span>
          <button type="button" data-testid={testid ? `${testid}-next-year` : undefined} onClick={() => setY(y + 1)} className="p-1 rounded hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS.map((m, i) => {
            const v = `${y}-${pad(i + 1)}`;
            const off = (min && v < min) || (max && v > max);
            return <button key={m} type="button" disabled={off} data-testid={testid ? `${testid}-option-${v}` : undefined} onClick={() => { emit(onChange, v); setOpen(false); }}
              className={`py-2 rounded-md text-sm capitalize transition-colors disabled:opacity-30 ${v === value ? "bg-slate-900 text-white font-bold" : "hover:bg-slate-100"}`}>{m}</button>;
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
